import { useState, Dispatch, SetStateAction, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { User, Settings, CreditCard, Shield, Bell, Key, Download, Trash2, AlertTriangle, Eye, EyeOff, Users } from 'lucide-react';
import PlansModal from "./PlansModal";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../lib/authFetch";
import { toast } from "sonner";

interface UserData {
  id: number;
  nome_completo: string;
  email: string;
  cpf: string;
  data_nascimento: string;
  telefone?: string;
  login_notification: boolean;
}

interface AccountSettingsProps {
  userData: UserData | null;
  setUserName: Dispatch<SetStateAction<string>>;
  onSubscriptionChange?: (subscription: any) => void;
}

const AccountSettings = ({ userData, setUserName, onSubscriptionChange }: AccountSettingsProps) => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formData, setFormData] = useState({
    nome_completo: userData?.nome_completo || '',
    email: userData?.email || '',
    cpf: userData?.cpf || '',
    telefone: userData?.telefone || ''
  });
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [loginNotification, setLoginNotification] = useState(userData?.login_notification ?? true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteFacialModal, setShowDeleteFacialModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [facialDeleteConfirmation, setFacialDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingFacial, setIsDeletingFacial] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<{
    id: number;
    plan: string;
    status: string;
    monthly_price: number;
    queries_limit: number;
    queries_used: number;
    auto_renew: boolean;
    start_date: string;
    next_payment_date: string | null;
  } | null>(null);

  // Atualizar formData quando userData mudar
  useEffect(() => {
    if (userData) {
      setFormData({
        nome_completo: userData.nome_completo || '',
        email: userData.email || '',
        cpf: userData.cpf || '',
        telefone: userData.telefone || ''
      });
    }
  }, [userData]);

  useEffect(() => {
    if (userData && typeof userData.login_notification === 'boolean') {
      setLoginNotification(userData.login_notification);
    }
  }, [userData]);

  // Buscar dados da assinatura
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/subscriptions/me`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setSubscriptionData(data);
        }
      } catch {
        // Erro ao buscar assinatura
      }
    };

    fetchSubscription();
  }, []);

  // Função para formatar data
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Não informado';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return 'Não informado';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/20 text-green-400">Pago</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400">Pendente</Badge>;
      case 'overdue':
        return <Badge className="bg-red-500/20 text-red-400">Vencido</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400">Desconhecido</Badge>;
    }
  };

  const handleSave = async () => {
    // Validação do telefone
    if (!formData.telefone || formData.telefone.length < 10) {
      toast.error('Telefone é obrigatório e deve ter pelo menos 10 dígitos');
      return;
    }
    if (formData.telefone.length > 11) {
      toast.error('Telefone deve ter no máximo 11 dígitos');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        // Atualize o localStorage e o estado do Dashboard
        localStorage.setItem('userName', formData.nome_completo);
        setUserName(formData.nome_completo);
        toast.success('Dados atualizados com sucesso!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro ao atualizar dados');
      }
    } catch {
      toast.error('Erro de conexão com o servidor');
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Preencha todos os campos de senha.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('A nova senha e a confirmação não coincidem.');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/users/me/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword
        })
      });
      if (response.ok) {
        toast.success('Senha alterada com sucesso!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro ao alterar senha');
      }
    } catch {
      toast.error('Erro de conexão com o servidor');
    }
  };

  const handleToggleLoginNotification = async (checked: boolean) => {
    setLoginNotification(checked);
    try {
      await fetch(`${API_BASE_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ login_notification: checked })
      });
    } catch (error) {
      toast.error('Erro ao atualizar preferência de notificação.');
    }
  };

  const handleDeleteFacialData = async () => {
    if (facialDeleteConfirmation !== 'EXCLUIR') {
      toast.error('Digite "EXCLUIR" para confirmar a exclusão dos dados faciais.');
      return;
    }
    setIsDeletingFacial(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/me/dados-faciais`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.ok) {
        toast.success('Dados faciais excluídos com sucesso (LGPD).');
        setShowDeleteFacialModal(false);
        setFacialDeleteConfirmation('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro ao excluir dados faciais');
      }
    } catch {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setIsDeletingFacial(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'EXCLUIR') {
      toast.error('Digite "EXCLUIR" para confirmar a exclusão da conta.');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Limpar dados locais
        localStorage.removeItem('token');
        localStorage.removeItem('userName');
        localStorage.removeItem('refresh_token');
        
        toast.success('Conta excluída com sucesso. Redirecionando...');
        setShowDeleteModal(false);
        setDeleteConfirmation('');
        
        // Redirecionar para a página inicial
        navigate('/');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro ao excluir conta');
      }
    } catch {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePauseSubscription = async () => {
    setIsPausing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/me/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data.subscription);
        toast.success('Assinatura pausada com sucesso! Redirecionando...');
        setShowPauseModal(false);
        
        // Limpar dados do localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('userName');
        localStorage.removeItem('userData');
        
        // Redirecionar para a tela de login
        navigate('/');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro ao pausar assinatura');
      }
    } catch {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setIsPausing(false);
    }
  };

  const handleResumeSubscription = async () => {
    setIsPausing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/me/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionData(data.subscription);
        toast.success('Assinatura reativada com sucesso!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro ao reativar assinatura');
      }
    } catch {
      toast.error('Erro de conexão com o servidor');
    } finally {
      setIsPausing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Information */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <User className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Informações do Perfil
          </CardTitle>
          <CardDescription className="text-gray-300 text-xs sm:text-sm">
            Gerencie suas informações pessoais e de contato
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <Label htmlFor="name" className="text-gray-300 text-sm">Nome Completo</Label>
              <Input
                id="name"
                value={formData.nome_completo}
                onChange={(e) => handleInputChange('nome_completo', e.target.value)}
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="email" className="text-gray-300 text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="cpf" className="text-gray-300 text-sm">CPF</Label>
              <Input
                id="cpf"
                value={formData.cpf}
                disabled
                className="bg-protec-dark border-protec-gold/30 text-gray-400 mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="phone" className="text-gray-300 text-sm">Telefone</Label>
              <Input
                id="phone"
                value={formData.telefone}
                onChange={(e) => {
                  // Só permite números e limita a 11 dígitos
                  const onlyNumbers = e.target.value.replace(/\D/g, '').slice(0, 11);
                  handleInputChange('telefone', onlyNumbers);
                }}
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
                placeholder="47999436423"
                maxLength={11}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.telefone?.length || 0}/11 dígitos</p>
            </div>
          </div>
          
          <div className="mt-4 sm:mt-6">
            <Button className="bg-protec-gold text-black hover:shadow-gold w-full sm:w-auto" onClick={handleSave}>
              Salvar Alterações
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Segurança
          </CardTitle>
          <CardDescription className="text-gray-300 text-xs sm:text-sm">
            Configure senha e opções de segurança da conta
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 sm:space-y-6">
            <div>
              <h4 className="text-white font-medium mb-3 sm:mb-4 text-sm sm:text-base">Alterar Senha</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="currentPassword" className="text-gray-300 text-sm">Senha Atual</Label>
                  <div className="relative">
                  <Input
                    id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                      className="bg-protec-dark border-protec-gold/30 text-white mt-1 pr-10"
                  />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-protec-gold transition-colors"
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="newPassword" className="text-gray-300 text-sm">Nova Senha</Label>
                  <div className="relative">
                  <Input
                    id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-protec-dark border-protec-gold/30 text-white mt-1 pr-10"
                  />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-protec-gold transition-colors"
                    >
                      {showNewPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="confirmPassword" className="text-gray-300 text-sm">Confirmar Senha</Label>
                  <div className="relative">
                  <Input
                    id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-protec-dark border-protec-gold/30 text-white mt-1 pr-10"
                  />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-protec-gold transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              <Button className="mt-4 bg-protec-gold text-black hover:shadow-gold w-full sm:w-auto" onClick={handleChangePassword}>
                <Key className="w-4 h-4 mr-2" />
                Alterar Senha
              </Button>
            </div>
            
            <div className="border-t border-protec-gold/20 pt-6">
              <h4 className="text-white font-medium mb-4">Opções de Segurança</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-white">Notificações de Login</h5>
                    <p className="text-gray-400 text-sm">Receber email quando houver login na conta</p>
                  </div>
                  <Switch checked={loginNotification} onCheckedChange={handleToggleLoginNotification} />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription & Billing */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Assinatura e Cobrança
          </CardTitle>
          <CardDescription className="text-gray-300 text-xs sm:text-sm">
            Gerencie sua assinatura e histórico de pagamentos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 sm:space-y-6">
            {(() => {
              const accessType = localStorage.getItem('access_type');
              const trialEndDate = localStorage.getItem('trial_end_date');
              
              // Acesso Liberado (gratuito permanente)
              if (accessType === 'free') {
                return (
                  <div className="bg-protec-dark rounded-lg p-3 sm:p-4 border border-green-500/20">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <h4 className="text-white font-semibold text-sm sm:text-base">Acesso Liberado</h4>
                        <p className="text-gray-400 text-xs sm:text-sm">Seu acesso foi liberado permanentemente</p>
                      </div>
                      <Badge className="self-start sm:self-auto text-xs bg-green-500/20 text-green-400">
                        ✓ Gratuito
                      </Badge>
                    </div>
                    <div className="mt-3 pt-3 border-t border-green-500/10">
                      <p className="text-green-400/80 text-xs">
                        Você tem acesso completo a todas as funcionalidades sem necessidade de pagamento.
                      </p>
                    </div>
                  </div>
                );
              }
              
              // Período de Teste (7 dias)
              if (accessType?.startsWith('trial') && trialEndDate) {
                const endDate = new Date(trialEndDate);
                const today = new Date();
                const diffTime = endDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                const isExpired = diffDays <= 0;
                
                return (
                  <div className={`bg-protec-dark rounded-lg p-3 sm:p-4 border ${isExpired ? 'border-red-500/20' : 'border-amber-500/20'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <h4 className="text-white font-semibold text-sm sm:text-base">Período de Teste</h4>
                        <p className="text-gray-400 text-xs sm:text-sm">
                          {isExpired 
                            ? 'Seu período de teste expirou' 
                            : `Válido até ${endDate.toLocaleDateString('pt-BR')}`
                          }
                        </p>
                      </div>
                      <Badge className={`self-start sm:self-auto text-xs ${
                        isExpired 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {isExpired ? 'Expirado' : `${diffDays} dia${diffDays > 1 ? 's' : ''} restante${diffDays > 1 ? 's' : ''}`}
                      </Badge>
                    </div>
                    <div className={`mt-3 pt-3 border-t ${isExpired ? 'border-red-500/10' : 'border-amber-500/10'}`}>
                      {isExpired ? (
                        <Button 
                          size="sm" 
                          className="bg-protec-gold text-black hover:bg-protec-gold-light text-xs"
                          onClick={() => setShowPlansModal(true)}
                        >
                          Escolher um Plano
                        </Button>
                      ) : (
                        <p className="text-amber-400/80 text-xs">
                          Aproveite o acesso completo durante o período de teste.
                        </p>
                      )}
                    </div>
                  </div>
                );
              }
              
              // Assinatura normal (paga via Mercado Pago)
              if (subscriptionData) {
                return (
                  <div className="bg-protec-dark rounded-lg p-3 sm:p-4 border border-protec-gold/10">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-2">
                      <div>
                        <h4 className="text-white font-semibold text-sm sm:text-base">Plano {subscriptionData.plan}</h4>
                        <p className="text-gray-400 text-xs sm:text-sm">Membro desde {formatDate(subscriptionData.start_date)}</p>
                      </div>
                      <Badge className={`self-start sm:self-auto text-xs ${
                        subscriptionData.status === 'suspended' 
                          ? 'bg-yellow-500/20 text-yellow-400' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {subscriptionData.status === 'suspended' ? 'Pausado' : 'Ativo'}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                      <div>
                        <span className="text-gray-400">Próx. pagamento:</span>
                        <p className="text-white truncate">{formatDate(subscriptionData.next_payment_date)}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Valor mensal:</span>
                        <p className="text-white">R$ {subscriptionData.monthly_price?.toFixed(2).replace('.', ',')}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Consultas:</span>
                        <p className="text-white">
                          {subscriptionData.queries_limit - subscriptionData.queries_used}/{subscriptionData.queries_limit}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-400">Renovação:</span>
                        <p className="text-white">
                          {subscriptionData.auto_renew ? 'Auto' : 'Manual'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-3 sm:mt-4">
                      <Button size="sm" variant="ghost" className="!border !border-protec-gold/50 !text-protec-gold hover:!bg-protec-gold/20 hover:!border-protec-gold text-xs h-8" onClick={() => setShowPlansModal(true)}>
                        Alterar Plano
                      </Button>
                    </div>
                  </div>
                );
              }
              
              // Loading ou sem dados
              return (
                <div className="bg-protec-dark rounded-lg p-4 border border-protec-gold/10 text-center">
                  <p className="text-gray-400 text-sm">Carregando dados da assinatura...</p>
                </div>
              );
            })()}
            
            {/* Histórico de Pagamentos - Comentado por enquanto
            <div>
              <h4 className="text-white font-medium mb-4">Histórico de Pagamentos</h4>
              <div className="space-y-3">
                {billingHistory.map((payment) => (
                  <div key={payment.id} className="bg-protec-dark rounded-lg p-3 border border-protec-gold/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-white font-medium">{payment.description}</h5>
                        <p className="text-gray-400 text-sm">
                          {payment.date} • Fatura: {payment.invoice}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-white font-semibold">{payment.amount}</span>
                        {getStatusBadge(payment.status)}
                        <Button size="sm" variant="outline" className="border-protec-gold text-protec-gold hover:bg-protec-gold hover:text-black">
                          <Download className="w-3 h-3 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            */}
          </div>
        </CardContent>
      </Card>

      {/* Account Actions */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Ações da Conta
          </CardTitle>
          <CardDescription className="text-gray-300 text-xs sm:text-sm">
            Exporte dados ou gerencie sua conta
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 sm:space-y-4">
            {/* Exportar Dados - Comentado por enquanto
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium">Exportar Dados</h4>
                <p className="text-gray-400 text-sm">Baixe todos os seus dados e histórico</p>
              </div>
              <Button variant="outline" className="border-protec-gold text-protec-gold hover:bg-protec-gold hover:text-black">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </div>
            */}
            
            {/* Pausar/Reativar Conta - Comentado por enquanto
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-white font-medium">
                  {subscriptionData?.status === 'suspended' ? 'Reativar Conta' : 'Pausar Conta'}
                </h4>
                <p className="text-gray-400 text-sm">
                  {subscriptionData?.status === 'suspended' 
                    ? 'Reativar sua assinatura para continuar usando a plataforma'
                    : 'Suspender temporariamente sua assinatura'
                  }
                </p>
              </div>
              <Button 
                variant="outline" 
                className={
                  subscriptionData?.status === 'suspended'
                    ? 'border-green-500 text-green-400 hover:bg-green-500 hover:text-black'
                    : 'border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black'
                }
                onClick={() => {
                  if (subscriptionData?.status === 'suspended') {
                    handleResumeSubscription();
                  } else {
                    setShowPauseModal(true);
                  }
                }}
                disabled={isPausing}
              >
                {isPausing ? 'Processando...' : (subscriptionData?.status === 'suspended' ? 'Reativar' : 'Pausar')}
              </Button>
            </div>
            */}
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 pb-3 border-b border-protec-gold/10">
              <div>
                <h4 className="text-white font-medium text-sm sm:text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-protec-gold" />
                  Excluir dados faciais (LGPD)
                </h4>
                <p className="text-gray-400 text-xs sm:text-sm">
                  Remove capturas, buscas, watchlist e biometria. Sua conta permanece ativa.
                </p>
              </div>
              <Button
                variant="ghost"
                className="!border !border-purple-500/40 !text-purple-300 hover:!bg-purple-500/10 h-8 sm:h-9 px-3"
                onClick={() => setShowDeleteFacialModal(true)}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Excluir faciais
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
              <div>
                <h4 className="text-white font-medium text-sm sm:text-base">Excluir Conta</h4>
                <p className="text-gray-400 text-xs sm:text-sm">Excluir permanentemente sua conta e dados</p>
              </div>
              <Button 
                variant="ghost" 
                className="!border !border-protec-gold/50 !text-protec-gold hover:!bg-protec-gold/20 hover:!border-protec-gold h-8 w-8 sm:w-auto sm:h-9 p-0 sm:px-3"
                onClick={() => setShowDeleteModal(true)}
              >
                <Trash2 className="w-4 h-4 !text-protec-gold" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <PlansModal
        open={showPlansModal}
        subscription={subscriptionData}
        onClose={() => setShowPlansModal(false)}
        onPlanChanged={(newSubscription) => {
          setSubscriptionData(newSubscription);
          if (onSubscriptionChange) {
            onSubscriptionChange(newSubscription);
          }
          setShowPlansModal(false);
        }}
      />

      {/* Modal exclusão dados faciais LGPD */}
      <Dialog open={showDeleteFacialModal} onOpenChange={setShowDeleteFacialModal}>
        <DialogContent className="bg-protec-dark-light border-purple-500/30">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              Excluir dados faciais
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Conforme a LGPD (Art. 11 — dados biométricos), você pode solicitar a exclusão de todas as capturas faciais,
              buscas, pessoas de interesse e arquivos de imagem associados à sua conta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Label htmlFor="facialDeleteConfirmation" className="text-gray-300">
              Digite <span className="text-purple-400 font-bold">EXCLUIR</span> para confirmar
            </Label>
            <Input
              id="facialDeleteConfirmation"
              value={facialDeleteConfirmation}
              onChange={(e) => setFacialDeleteConfirmation(e.target.value)}
              className="bg-protec-dark border-purple-500/30 text-white"
              placeholder="EXCLUIR"
            />
            <Button
              onClick={handleDeleteFacialData}
              disabled={facialDeleteConfirmation !== 'EXCLUIR' || isDeletingFacial}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isDeletingFacial ? 'Excluindo...' : 'Confirmar exclusão de dados faciais'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-protec-dark-light border-red-500/30">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Excluir Conta Permanentemente
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Esta ação é irreversível. Todos os seus dados, incluindo consultas, alertas, investigações e relatórios serão excluídos permanentemente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <h4 className="text-red-400 font-medium mb-2">⚠️ Aviso Importante</h4>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>• Sua conta será excluída permanentemente</li>
                <li>• Todos os dados serão perdidos</li>
                <li>• Sua assinatura será cancelada</li>
                <li>• Não será possível recuperar os dados</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="deleteConfirmation" className="text-gray-300">
                Digite "EXCLUIR" para confirmar
              </Label>
              <Input
                id="deleteConfirmation"
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className="bg-protec-dark border-red-500/30 text-white mt-1"
                placeholder="Digite EXCLUIR"
              />
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button 
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation !== 'EXCLUIR' || isDeleting}
                className="bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
              >
                {isDeleting ? 'Excluindo...' : 'Excluir Conta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Pausa */}
      <Dialog open={showPauseModal} onOpenChange={setShowPauseModal}>
        <DialogContent className="bg-protec-dark-light border-yellow-500/30">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Pausar Assinatura
            </DialogTitle>
            <DialogDescription className="text-gray-300">
              Sua assinatura será pausada temporariamente. Você poderá reativá-la a qualquer momento.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
              <h4 className="text-yellow-400 font-medium mb-2">ℹ️ O que acontece quando você pausa:</h4>
              <ul className="text-gray-300 text-sm space-y-1">
                <li>• Sua assinatura será suspensa temporariamente</li>
                <li>• Não haverá cobranças durante a pausa</li>
                <li>• Seus dados serão preservados</li>
                <li>• Você pode reativar a qualquer momento</li>
                <li>• Acesso às funcionalidades será limitado</li>
                <li>• Você será desconectado e redirecionado para o login</li>
              </ul>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button 
                onClick={handlePauseSubscription}
                disabled={isPausing}
                className="bg-yellow-500 text-black hover:bg-yellow-600 disabled:opacity-50"
              >
                {isPausing ? 'Pausando...' : 'Pausar Assinatura'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountSettings;
